import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ky from 'ky';
import styled from 'styled-components';
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { Grid, Card, CardContent, CardActions } from '@material-ui/core';
import { Typography, IconButton, TextField, Button } from '@material-ui/core';
import { List, ListItem, ListItemText, ListItemIcon, Divider } from '@material-ui/core';
import { Select, Menu, MenuItem } from '@material-ui/core';
import { Checkbox } from '@material-ui/core';
import StoreOutlinedIcon from '@material-ui/icons/StoreOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';

import { getLoginCheckData, setSnackbar } from '../redux/workBackendSlice';
import { GoodsStatusType, isGoodsStatusType, GoodsDb, GoodsDbHashType, ShopState, closeShop, getPublicGoods, getOwnGoods, getPurchases } from '../redux/shopSlice';
import useShopState from '../hooks/useShopState';
import useWorkBackend from '../hooks/useWorkBackend';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useAllFoldersDb from '../hooks/useAllFoldersDb';
import useAllPaletteDbs from '../hooks/useAllPaletteDbs';
import useInstallGoods from '../hooks/useInstallGoods';
import useGetInstalledGoods from '../hooks/useGetInstalledGoods';

import { apiURL } from '../components/DigimagiProvider';
import { Hash, deepCopy, setTokensToCookie } from '../general';
import { formatVersion } from '.';
import DeleteGoodsDialog from './DeleteGoodsDialog';
import EditPaletteGoodsDialog from './EditPaletteGoodsDialog';

const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 24px;
  top: 24px;
  z-index: 999;
`
const HeaderWrapper = styled.div`
  position: sticky;
  top: 0px;
  z-index: 998;
  background-color: #ffffff;
`
type GoodsCardProps = {
  $top: number;
}
const GoodsCard = styled(Card)<GoodsCardProps>`
  position: sticky;
  margin: 8px;
  top: ${props => `${props.$top}px`};
`

type ShowDataType = { id: string; goods: GoodsDb, items: Hash<Object[]> }

type GoodsListProps = {
  shopState: ShopState;
  showData?: ShowDataType;
  toggleShowData: (list: GoodsDbHashType, id?: string) => void;
}
const GoodsList: React.FC<GoodsListProps> = ({ shopState, showData, toggleShowData }) => {
  const dispatch = useDispatch();
  const installGoods = useInstallGoods();
  const userId = useCurrentUserId();
  const [folderIds, folders] = useAllFoldersDb(userId);
  const [paletteIds, palettes] = useAllPaletteDbs(userId);
  const goodsKinds: Hash<string> = { 'book': 'ブック', 'card_template': 'カードテンプレート', 'palette': 'パレット' };
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();

  const getInstalled = (id: string) => {
    return Boolean(Object.keys(shopState.purchases).find(targetId => shopState.purchases[targetId].goods_id === id));
  }
  const getInstallMessage = () => {
    if (!showData) return '';
    const where = showData.goods.kind === 'book' ? 'フォルダに' : showData.goods.kind === 'card_template' ? 'パレットに' : '';
    return getInstalled(showData.id) ? `${where}追加する` : 'インストールする';
  }
  const openInstallMenu = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (showData?.goods.kind === 'palette') installGoodsExe();
    else setMenuAnchorEl(ev.currentTarget);
  }
  const installGoodsExe = async (parentId?: string) => {
    if (!showData) return;
    try {
      const list: [string, number | 'latest'][] = [[showData.id, showData.goods.latest_version]];
      const sendData = { list };
      await installGoods({ sendData, [showData.goods.kind === 'book' ? 'folderId' : 'paletteId']: parentId });
      dispatch(setSnackbar({ open: true, message: `"${showData.goods.name}" をインストールしました` }));
      setMenuAnchorEl(undefined);
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (!showData) return;
    toggleShowData(shopState.goods, showData.id)
  }, [shopState])

  return (
    <>
      <HeaderWrapper>
        <DialogTitle disableTypography>
          <Typography variant="h6">デジマジショップ</Typography>
        </DialogTitle>
      </HeaderWrapper>
      { Object.keys(shopState.goods).length === 0 && <Typography>商品がありません</Typography> }
      <Grid container>
        <Grid item xs={showData ? 6 : 11}>
          <List>
            { Object.keys(shopState.goods).map((id, ix) => {
              const value = shopState.goods[id];
              return (
                <div key={ix}>
                  <ListItem key={ix} button selected={id === showData?.id} onClick={() => toggleShowData(shopState.goods, id)}>
                    <ListItemText primary={value.name} />
                    <ListItemText secondary={`v${formatVersion(value.latest_version)}`} />
                    <ListItemText secondary={goodsKinds[value.kind]} />
                    { getInstalled(id) && <ListItemText secondary="インストール済み" /> }
                  </ListItem>
                  { ix + 1 < Object.keys(shopState.goods).length && <Divider /> }
                </div>
              );
            })}
          </List>
        </Grid>
        <Grid item xs={showData ? 6 : 1}>
          { showData &&
            <GoodsCard elevation={3} $top={72}>
              <CardContent>
                <Grid container direction="column" spacing={1}>
                  <Grid item><Typography>{ showData.goods.name }</Typography></Grid>
                  <Grid item><Typography>{ goodsKinds[showData.goods.kind] }</Typography></Grid>
                  { showData.goods.kind === 'palette' &&
                    <Grid item>
                      <List>
                        { showData.items.card_templates.map((data, ix) => {
                          if (!('jdoc' in data) || typeof data.jdoc !== 'string' || !('version' in data) || typeof data.version !== 'number') return;
                          const value = JSON.parse(data.jdoc);
                          return (
                            <div key={ix}>
                              <ListItem key={ix} dense>
                                <ListItemText primary={value.name} />
                                <ListItemText secondary={`v${formatVersion(data.version)}`} />
                              </ListItem>
                              { ix + 1 < Object.keys(showData.items.card_templates).length && <Divider /> }
                            </div>
                          );
                        }) }
                      </List>
                    </Grid>
                  }
                  <Grid item><Typography>{ showData.goods.description }</Typography></Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Button color="secondary" onClick={() => toggleShowData(shopState.goods)}>閉じる</Button>
                <Button color="primary" onClick={openInstallMenu}>{ getInstallMessage() }</Button>
                <Menu
                  anchorEl={menuAnchorEl}
                  keepMounted
                  open={Boolean(menuAnchorEl)}
                  onClose={() => setMenuAnchorEl(undefined)}
                >
                  { showData.goods.kind === 'book' && folderIds.map((id, ix) => {
                    const folder = folders[ix];
                    return (
                      <MenuItem key={id} onClick={() => installGoodsExe(id)}>{ folder.name }</MenuItem>
                    );
                  }) }
                  { showData.goods.kind === 'card_template' && paletteIds.map((id, ix) => {
                    const palette = palettes[ix];
                    if (palette.standard) return;
                    return (
                      <MenuItem key={id} onClick={() => installGoodsExe(id)}>{ palette.name }</MenuItem>
                    );
                  }) }
                </Menu>
              </CardActions>
            </GoodsCard>
          }
        </Grid>
      </Grid>
    </>
  );
}

type InstalledGoodsListProps = {
  shopState: ShopState;
}
const InstalledGoodsList: React.FC<InstalledGoodsListProps> = ({ shopState }) => {
  const [ids, values] = useGetInstalledGoods();
  const [showData, setShowData] = useState<GoodsDb & { id: string }>();
  const goodsKinds: Hash<string> = { 'book': 'ブック', 'card_template': 'カードテンプレート', 'palette': 'パレット' };

  const toggleShowData = (id?: string, goodsId?: string) => {
    if (!id || !goodsId || (showData && id === showData.id)) setShowData(undefined);
    else {
      const data = shopState.goods[goodsId];
      setShowData({ id, ...data });
    }
  }

  useEffect(() => {
    if (!showData) return;
    const data = shopState.goods[showData.id];
    setShowData({ id: showData.id, ...data });
  }, [shopState])

  return (
    <>
      <HeaderWrapper>
        <DialogTitle disableTypography>
          <Typography variant="h6">インストールした商品</Typography>
        </DialogTitle>
      </HeaderWrapper>
      { ids.length === 0 && <Typography>インストールした商品がありません</Typography> }
      <Grid container>
        <Grid item xs={showData ? 6 : 11}>
          <List>
            { ids.map((id, ix) => {
              const value = values[ix];
              return (
                <div key={ix}>
                  <ListItem key={ix} button selected={id === showData?.id} onClick={() => toggleShowData(id, value.goodsId)}>
                    <ListItemText primary={value.name} />
                    <ListItemText secondary={`v${value.version ? formatVersion(value.version) : '000000'}`} />
                    <ListItemText secondary={value.place} />
                  </ListItem>
                  { ix + 1 < ids.length && <Divider /> }
                </div>
              );
            })}
          </List>
        </Grid>
        <Grid item xs={showData ? 6 : 1}>
          { showData &&
            <GoodsCard elevation={3} $top={72}>
              <CardContent>
                <Grid container direction="column" spacing={1}>
                  <Grid item><Typography>{ showData.name }</Typography></Grid>
                  <Grid item><Typography>{ goodsKinds[showData.kind] }</Typography></Grid>
                  <Grid item><Typography>{ showData.description }</Typography></Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Button color="secondary" onClick={() => setShowData(undefined)}>閉じる</Button>
              </CardActions>
            </GoodsCard>
          }
        </Grid>
      </Grid>
    </>
  );
}

type OwnGoodsListProps = {
  shopState: ShopState;
  setShopLatest: (value: boolean) => void;
  showData?: ShowDataType;
  toggleShowData: (list: GoodsDbHashType, id?: string) => void;
}
const OwnGoodsList: React.FC<OwnGoodsListProps> = ({ shopState, setShopLatest, showData, toggleShowData }) => {
  const dispatch = useDispatch();
  const publishStatus: Hash<string> = { 'public': '公開', 'private': '限定公開', 'closed': '非公開' };
  const goodsKinds: Hash<string> = { 'book': 'ブック', 'card_template': 'カードテンプレート', 'palette': 'パレット' };
  const [deleteGoodsAction, setDeleteGoodsAction] = useState<{ id: string; name: string; }>();
  const [editPaletteGoodsAction, setEditPaletteGoodsAction] = useState<{ open: boolean; id?: string }>({ open: false });
  const [name, setName] = useState<string>();
  const [status, setStatus] = useState<GoodsStatusType>();

  const getExeDisabled = () => {
    if (!showData) return true;
    if (name && name !== showData.goods.name) return false;
    if (status && status !== showData.goods.status) return false;
    return true;
  }
  const selectStatus = (value: unknown) => {
    if (isGoodsStatusType(value)) {
      setStatus(value);
    }
  }
  const changeGoodsExe = async (id?: string) => {
    if (!id || !showData || id !== showData.id) return;
    try {
      const sendData: { [P in keyof GoodsDb]?: GoodsDb[P]; } & { goods_id: string; } = { goods_id: id };
      let changeFlag = false;
      if (name && name !== showData.goods.name) {
        sendData.name = name;
        changeFlag = true;
      }
      if (status && status !== showData.goods.status) {
        sendData.status = status;
        changeFlag = true;
      }
      if (!changeFlag) return;
      const loginCheckData = getLoginCheckData();
      const query = await ky.post(`${apiURL}save_goods_data`, { json: { ...sendData, ...loginCheckData } }).json() as any;
      const newToken = query.data;
      if (newToken && typeof newToken === 'object') setTokensToCookie(newToken);
      setShopLatest(false);
      dispatch(setSnackbar({ open: true, message: `"${name}" 変更を保存しました` }));
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (!showData) return;
    toggleShowData(shopState.ownGoods, showData.id)
  }, [shopState])

  useEffect(() => {
    if (!showData) {
      setName(undefined);
      setStatus(undefined);
      return;
    }
    setName(showData.goods.name);
    setStatus(showData.goods.status);
  }, [showData])

  return (
    <>
      <HeaderWrapper>
        <DialogTitle disableTypography>
          <Typography variant="h6">商品化したアイテム</Typography>
        </DialogTitle>
        <Button variant="outlined" color="primary" onClick={() => setEditPaletteGoodsAction({ open: true })}>パレットを作成する</Button>
      </HeaderWrapper>
      { Object.keys(shopState.ownGoods).length === 0 && <Typography>商品化したアイテムがありません</Typography> }
      <Grid container>
        <Grid item xs={showData ? 6 : 11}>
          <List>
            { Object.keys(shopState.ownGoods).map((id, ix) => {
              const value = shopState.ownGoods[id];
              return (
                <div key={ix}>
                  <ListItem key={ix} button selected={id === showData?.id} onClick={() => toggleShowData(shopState.ownGoods, id)}>
                    <ListItemText primary={value.name} />
                    <ListItemText secondary={`v${formatVersion(value.latest_version)}`} />
                    <ListItemText secondary={value.deleted_at ? '削除済み' : publishStatus[value.status]} />
                    <ListItemText secondary={goodsKinds[value.kind]} />
                  </ListItem>
                  { ix + 1 < Object.keys(shopState.ownGoods).length && <Divider /> }
                </div>
              );
            }) }
          </List>
        </Grid>
        <Grid item xs={showData ? 6 : 1}>
          { (showData && status) &&
            <GoodsCard elevation={3} $top={108.5}>
              <CardContent>
                <Grid container direction="column" spacing={1}>
                  <Grid item><TextField label="商品名" variant="outlined" value={name} onChange={ev => setName(ev.currentTarget.value)} fullWidth /></Grid>
                  <Grid item>
                    <Select value={status} onChange={ev => selectStatus(ev.target.value)}>
                      { Object.keys(publishStatus).map(key =>
                        <MenuItem value={key}>{ publishStatus[key] }</MenuItem>
                      ) }
                    </Select>
                  </Grid>
                  { showData.goods.kind === 'palette' &&
                    <Grid item>
                      <List>
                        { showData.items.card_templates.map((data, ix) => {
                          if (!('jdoc' in data) || typeof data.jdoc !== 'string' || !('version' in data) || typeof data.version !== 'number') return;
                          const value = JSON.parse(data.jdoc);
                          return (
                            <div key={ix}>
                              <ListItem key={ix} dense>
                                <ListItemText primary={value.name} />
                                <ListItemText secondary={`v${formatVersion(data.version)}`} />
                              </ListItem>
                              { ix + 1 < Object.keys(showData.items.card_templates).length && <Divider /> }
                            </div>
                          );
                        }) }
                      </List>
                    </Grid>
                  }
                </Grid>
                <IconButton onClick={() => {
                  if (!showData.id) return;
                  setDeleteGoodsAction({ id: showData.id, name: showData.goods.name });
                }}>
                  <DeleteIcon />
                </IconButton>
              </CardContent>
              <CardActions>
                <Button color="secondary" onClick={() => toggleShowData(shopState.ownGoods)}>閉じる</Button>
                <Button color="primary" disabled={getExeDisabled()} onClick={() => changeGoodsExe(showData.id)}>変更を保存する</Button>
              </CardActions>
            </GoodsCard>
          }
        </Grid>
      </Grid>
      <DeleteGoodsDialog onClose={() => {
          setDeleteGoodsAction(undefined);
          setShopLatest(false);
        }}
        data={deleteGoodsAction}
      />
      <EditPaletteGoodsDialog
        open={editPaletteGoodsAction.open}
        onClose={() => {
          setEditPaletteGoodsAction({ open: false });
          setShopLatest(false);
        }}
        id={editPaletteGoodsAction.id}
      />
    </>
  );
}

type InitialSetupListProps = {
  shopState: ShopState;
  setShopLatest: (value: boolean) => void;
  showData?: ShowDataType;
  toggleShowData: (list: GoodsDbHashType, id?: string) => void;
}
const InitialSetupList: React.FC<InitialSetupListProps> = ({ shopState, setShopLatest, showData, toggleShowData }) => {
  const dispatch = useDispatch();
  const publishStatus: Hash<string> = { 'public': '公開', 'private': '限定公開', 'closed': '非公開' };
  const [deleteGoodsAction, setDeleteGoodsAction] = useState<{ id: string; name: string; }>();
  const [name, setName] = useState<string>();
  const [status, setStatus] = useState<GoodsStatusType>();
  const [initials, setInitials] = useState<[string, number][]>([]);
  const [checks, setChecks] = useState<Hash<boolean>>();
  const [stateLatest, setStateLatest] = useState(false);

  const changeChecked = (id: string, value: boolean) => {
    if (!checks) return;
    const newChecks = deepCopy(checks);
    newChecks[id] = value;
    setChecks(newChecks);
  }
  const getCheckChanged = (id: string) => {
    if (!checks) return;
    const isInitial = Boolean(initials.find(data => data[0] === id));
    return isInitial !== checks[id];
  }
  const getExeDisabled = () => {
    if (!showData) return true;
    if (name && name !== showData.goods.name) return false;
    if (status && status !== showData.goods.status) return false;
    return true;
  }
  const selectStatus = (value: unknown) => {
    if (isGoodsStatusType(value)) {
      setStatus(value);
    }
  }
  const changeGoodsExe = async (id?: string) => {
    if (!id || !showData || id !== showData.id) return;
    try {
      const sendData: { [P in keyof GoodsDb]?: GoodsDb[P]; } & { goods_id: string; } = { goods_id: id };
      let changeFlag = false;
      if (name && name !== showData.goods.name) {
        sendData.name = name;
        changeFlag = true;
      }
      if (status && status !== showData.goods.status) {
        sendData.status = status;
        changeFlag = true;
      }
      if (!changeFlag) return;
      const loginCheckData = getLoginCheckData();
      const query = await ky.post(`${apiURL}save_goods_data`, { json: { ...sendData, ...loginCheckData } }).json() as any;
      const newToken = query.data;
      if (newToken && typeof newToken === 'object') setTokensToCookie(newToken);
      setShopLatest(false);
      dispatch(setSnackbar({ open: true, message: `"${name}" 変更を保存しました` }));
    } catch (err) {
      console.log(err);
    }
  }
  const updateInitialSetupData = async () => {
    try {
      if (!checks) return;
      const initialIds = Object.keys(shopState.ownGoods).filter(id => checks[id]);
      const goodsList = initialIds.map(id => [id, shopState.ownGoods[id].latest_version]);
      const sendData = { goods_list: goodsList };
      const loginCheckData = getLoginCheckData();
      const query = await ky.post(`${apiURL}update_initial_setup_data`, { json: { ...sendData, ...loginCheckData } }).json() as any;
      setStateLatest(false);
      dispatch(setSnackbar({ open: true, message: `初期データを更新しました` }));
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (stateLatest) return;
    const setInitialAndChecks = async () => {
      const query = await ky.get(`${apiURL}get_initial_setup_data`).json() as any;
      const goodsList = JSON.parse(query.data.goods_list) as [string, number][];
      setInitials(goodsList);

      const goodsIds = Object.keys(shopState.ownGoods).filter(id => !shopState.ownGoods[id].deleted_at);
      const newChecks: Hash<boolean> = {};
      goodsIds.forEach(id => {
        const checked = Boolean(goodsList.find(data => data[0] === id));
        newChecks[id] = checked;
      })
      setChecks(newChecks);

      setStateLatest(true);
    }
    setInitialAndChecks();
  }, [shopState.open, stateLatest])

  useEffect(() => {
    if (!showData) return;
    toggleShowData(shopState.ownGoods, showData.id);
  }, [shopState])

  useEffect(() => {
    if (!showData) {
      setName(undefined);
      setStatus(undefined);
      return;
    }
    setName(showData.goods.name);
    setStatus(showData.goods.status);
  }, [showData])

  return (
    <>
      <HeaderWrapper>
        <DialogTitle disableTypography>
          <Typography variant="h6">初期データセットアップ</Typography>
        </DialogTitle>
        <Button variant="outlined" color="primary" onClick={updateInitialSetupData}>初期データを更新する</Button>
      </HeaderWrapper>
      { Object.keys(shopState.ownGoods).length === 0 && <Typography>商品化したアイテムがありません</Typography> }
      <Grid container>
        <Grid item xs={showData ? 6 : 11}>
          <List>
            { Object.keys(shopState.ownGoods).map((id, ix) => {
              const value = shopState.ownGoods[id];
              if (value.deleted_at) return;
              return (
                <div key={ix}>
                  <ListItem key={ix} button selected={id === showData?.id} onClick={() => toggleShowData(shopState.ownGoods, id)} style={{ backgroundColor: getCheckChanged(id) ? id === showData?.id ? '#61b8ff' : '#61b8ff64' : undefined }}>
                    { checks && <Checkbox color="default" size="small" checked={checks[id]} onChange={ev => changeChecked(id, ev.target.checked)} /> }
                    <ListItemText primary={value.name} />
                    <ListItemText secondary={`v${formatVersion(value.latest_version)}`} />
                    <ListItemText secondary={value.deleted_at ? '削除済み' : publishStatus[value.status]} />
                  </ListItem>
                  { ix + 1 < Object.keys(shopState.ownGoods).length && <Divider /> }
                </div>
              );
            })}
          </List>
        </Grid>
        <Grid item xs={showData ? 6 : 1}>
          { (showData && status) &&
            <GoodsCard elevation={3} $top={108.5}>
              <CardContent>
                <Grid container direction="column" spacing={1}>
                  <Grid item><TextField label="商品名" variant="outlined" value={name} onChange={ev => setName(ev.currentTarget.value)} fullWidth /></Grid>
                  <Grid item>
                    <Select value={status} onChange={ev => selectStatus(ev.target.value)}>
                      { Object.keys(publishStatus).map(key =>
                        <MenuItem value={key}>{ publishStatus[key] }</MenuItem>
                      ) }
                    </Select>
                  </Grid>
                  { showData.goods.kind === 'palette' &&
                    <Grid item>
                      <List>
                        { showData.items.card_templates.map((data, ix) => {
                          if (!('jdoc' in data) || typeof data.jdoc !== 'string' || !('version' in data) || typeof data.version !== 'number') return;
                          const value = JSON.parse(data.jdoc);
                          return (
                            <div key={ix}>
                              <ListItem key={ix} dense>
                                <ListItemText primary={value.name} />
                                <ListItemText secondary={`v${formatVersion(data.version)}`} />
                              </ListItem>
                              { ix + 1 < Object.keys(showData.items.card_templates).length && <Divider /> }
                            </div>
                          );
                        }) }
                      </List>
                    </Grid>
                  }
                </Grid>
                <IconButton onClick={() => {
                  if (!showData.id) return;
                  setDeleteGoodsAction({ id: showData.id, name: showData.goods.name });
                }}>
                  <DeleteIcon />
                </IconButton>
              </CardContent>
              <CardActions>
                <Button color="secondary" onClick={() => toggleShowData(shopState.ownGoods)}>閉じる</Button>
                <Button color="primary" disabled={getExeDisabled()} onClick={() => changeGoodsExe(showData.id)}>変更を保存する</Button>
              </CardActions>
            </GoodsCard>
          }
        </Grid>
      </Grid>
      <DeleteGoodsDialog onClose={() => {
          setDeleteGoodsAction(undefined);
          setShopLatest(false);
        }}
        data={deleteGoodsAction}
      />
    </>
  );
}

const ShopDialog: React.FC = () => {
  const dispatch = useDispatch();
  const shopState = useShopState();
  const authUser = useWorkBackend().authUser;
  const [shopLatest, setShopLatest] = useState(false);
  const [menuIx, setMenuIx] = useState(0);
  const [showData, setShowData] = useState<ShowDataType>();

  const onClose = () => {
    dispatch(closeShop());
  }
  const toggleShowData = async (list: GoodsDbHashType, id?: string) => {
    if (!id || (showData && id === showData.id)) setShowData(undefined);
    else {
      const data = list[id];
      const query = await ky.post(`${apiURL}get_goods_items`, { json: { list: [[id, data.latest_version]] } }).json() as any;
      const items = query.data;
      setShowData({ id, goods: data, items });
    }
  }

  useEffect(() => {
    dispatch(getPublicGoods());
    dispatch(getOwnGoods());
    dispatch(getPurchases());
    setShopLatest(true);
  }, [shopState.open])

  useEffect(() => {
    if (shopLatest) return;
    dispatch(getPublicGoods());
    dispatch(getOwnGoods());
    dispatch(getPurchases());
    setShopLatest(true);
  }, [shopLatest])

  return (
    <Dialog open={shopState.open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogContent>
        <CloseIconButton onClick={onClose}><CloseIcon /></CloseIconButton>
        <Grid container>
          <Grid item xs={3} style={{ borderRight: 'solid 1px lightgrey', paddingRight: 8 }}>
            <List style={{ position: 'sticky', top: 0 }}>
              <ListItem key={0} button selected={menuIx === 0} onClick={() => setMenuIx(0)}>
                <ListItemIcon><StoreOutlinedIcon /></ListItemIcon>
                <ListItemText primary="デジマジショップ" />
              </ListItem>
              <Divider />
              <ListItem key={1} button selected={menuIx === 1} onClick={() => setMenuIx(1)}>
                <ListItemIcon><GetAppOutlinedIcon /></ListItemIcon>
                <ListItemText primary="インストールした商品" />
              </ListItem>
              <Divider />
              <ListItem key={2} button selected={menuIx === 2} onClick={() => setMenuIx(2)}>
                <ListItemIcon><EditOutlinedIcon /></ListItemIcon>
                <ListItemText primary="商品化したアイテム" />
              </ListItem>
              { (authUser && authUser !== 'noAuth' && authUser.uid === process.env.REACT_APP_MANAGER_USER_ID) &&
                <div>
                  <Divider />
                  <ListItem key={3} button selected={menuIx === 3} onClick={() => setMenuIx(3)}>
                    <ListItemText primary="初期データセットアップ" />
                  </ListItem>
                </div>
              }
            </List>
          </Grid>
          <Grid item xs={9} style={{ paddingLeft: 8 }}>
            { menuIx === 0 && <GoodsList shopState={shopState} showData={showData} toggleShowData={toggleShowData} /> }
            { menuIx === 1 && <InstalledGoodsList shopState={shopState} /> }
            { menuIx === 2 && <OwnGoodsList shopState={shopState} setShopLatest={setShopLatest} showData={showData} toggleShowData={toggleShowData} /> }
            { (menuIx === 3 && authUser && authUser !== 'noAuth' && authUser.uid === process.env.REACT_APP_MANAGER_USER_ID) &&
              <InitialSetupList shopState={shopState} setShopLatest={setShopLatest} showData={showData} toggleShowData={toggleShowData} />
            }
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default ShopDialog;
